// The floor's walk-in warning (F-RSV-N1). Staff may still open a table that
// is held for a booking — a restaurant sometimes has to — but they see who it
// is held for and decide. `guard(table, proceed)` runs proceed straight away
// when the table isn't held; otherwise it asks first.
import { useState, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useReservationHolds } from '@/hooks/useReservations'
import { pktTime, type ReservationHold } from '@/lib/reservations'

type GuardTable = { id: string; table_number: string }

export function useReservedTableGuard(): {
  holds: Map<string, ReservationHold>
  guard: (table: GuardTable, proceed: () => void) => void
  dialog: ReactNode
} {
  const holds = useReservationHolds()
  const [pending, setPending] = useState<{ table: GuardTable; hold: ReservationHold; proceed: () => void } | null>(null)

  const guard = (table: GuardTable, proceed: () => void) => {
    const hold = holds.get(table.id)
    if (!hold) {
      proceed()
      return
    }
    setPending({ table, hold, proceed })
  }

  const dialog = (
    <Dialog open={Boolean(pending)} onOpenChange={(o) => !o && setPending(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
            Table {pending?.table.table_number} is reserved
          </DialogTitle>
          <DialogDescription>
            {pending
              ? `Booked for ${pending.hold.guest_name} (${pending.hold.party_size} ${pending.hold.party_size === 1 ? 'guest' : 'guests'}) at ${pktTime(pending.hold.starts_at)}. Open it for someone else anyway?`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPending(null)}>
            Keep it free
          </Button>
          <Button
            onClick={() => {
              const p = pending
              setPending(null)
              p?.proceed()
            }}
          >
            Open anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { holds, guard, dialog }
}
